(function () {
  if (customElements.get('onhow-photo-upload')) return;

  class OnhowPhotoUpload extends HTMLElement {
    constructor() {
      super();
      this.files = [];
      this.required = 1;
      this.form = null;
      this.config = {};
      this.objectUrls = [];
      this.activeVariantId = '';
      this.currentQuantity = 1;
      this.eventController = new AbortController();
      this.fileMeta = new WeakMap();
      this.replaceIndex = null;
      this.dragIndex = null;
      this.dimensionRenderTimer = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const configEl = document.querySelector('[data-onhow-upload-config]');
      this.config = configEl ? configEl.dataset : {};
      this.variants = this.readJson('[data-variant-requirements]');
      this.input = this.querySelector('[data-upload-input]');
      this.zone = this.querySelector('[data-upload-zone]');
      this.previews = this.querySelector('[data-upload-previews]');
      this.error = this.querySelector('[data-upload-error]');
      this.count = this.querySelector('[data-upload-count]');
      this.complete = this.querySelector('[data-upload-complete]');
      this.repeatToggle = this.querySelector('[data-repeat-toggle]');
      this.repeatHelp = this.querySelector('[data-repeat-help]');

      this.applyConfig();
      this.scope = this.closest('product-component, product-information, featured-product-information, .shopify-section') || this.parentElement;
      this.form = this.findProductForm();
      if (!this.form) {
        this.showError('Upload setup error: the product form could not be found.');
        return;
      }
      this.form.enctype = 'multipart/form-data';
      this.activeVariantId = this.findVariantId();
      this.currentQuantity = this.findQuantity();
      this.bindEvents();
      this.updateRequirement();
      this.observePaymentButton();
    }

    disconnectedCallback() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.observer?.disconnect();
      this.eventController?.abort();
      clearTimeout(this.dimensionRenderTimer);
    }

    readJson(selector) {
      try { return JSON.parse(this.querySelector(selector).textContent); }
      catch (error) { return {}; }
    }

    applyConfig() {
      this.style.setProperty('--onhow-primary', this.config.primary || '#000000');
      this.style.setProperty('--onhow-background', this.config.background || '#f7f7f7');
      this.style.setProperty('--onhow-radius', `${this.config.radius || 8}px`);
      this.querySelector('[data-upload-label]').textContent = `${this.config.label || 'Photo'}s`;
      this.querySelector('[data-upload-helper]').textContent = this.config.helperText || 'Drag and drop or choose your photos';
      this.querySelector('[data-upload-button]').textContent = this.config.buttonText || 'Choose photos';
      this.querySelector('[data-upload-privacy]').textContent = this.config.privacyText || 'Your photos are used only to produce your order.';
    }

    findProductForm() {
      const selectors = 'form[action*="/cart/add"], form[data-type="add-to-cart-form"]';
      return this.scope?.querySelector(selectors) || document.querySelector(selectors);
    }

    bindEvents() {
      this.zone.addEventListener('click', () => this.input.click());
      this.zone.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.input.click(); }
      });
      this.input.addEventListener('change', () => {
        const selected = Array.from(this.input.files || []);
        if (this.replaceIndex !== null) this.replaceFile(this.replaceIndex, selected[0]);
        else this.addFiles(selected);
        this.replaceIndex = null;
        this.input.value = '';
      });
      this.repeatToggle.addEventListener('change', () => {
        this.repeatHelp.hidden = !this.repeatToggle.checked;
        this.redistributeCopies();
        this.clearError();
        this.render();
      });
      ['dragenter', 'dragover'].forEach((name) => this.zone.addEventListener(name, (event) => {
        event.preventDefault(); this.zone.classList.add('is-dragging');
      }));
      ['dragleave', 'drop'].forEach((name) => this.zone.addEventListener(name, (event) => {
        event.preventDefault(); this.zone.classList.remove('is-dragging');
      }));
      this.zone.addEventListener('drop', (event) => this.addFiles(Array.from(event.dataTransfer.files || [])));

      document.addEventListener('change', (event) => {
        if (!this.scope?.contains(event.target)) return;
        const variantPicker = event.target.closest('variant-picker');
        if (variantPicker) {
          const selected = event.target instanceof HTMLSelectElement
            ? event.target.options[event.target.selectedIndex]
            : event.target;
          if (selected?.dataset?.variantId) this.activeVariantId = String(selected.dataset.variantId);
          this.updateRequirement();
          // Horizon updates parts of the product UI asynchronously after selection.
          setTimeout(() => { this.activeVariantId = this.findVariantId() || this.activeVariantId; this.updateRequirement(); }, 500);
          return;
        }
        if (event.target.name === 'id') this.activeVariantId = String(event.target.value || '');
        if (event.target.name === 'quantity' || event.target.closest('quantity-selector')) {
          this.currentQuantity = Math.max(1, parseInt(event.target.value, 10) || this.findQuantity());
        }
        this.updateRequirement();
      }, { capture: true, signal: this.eventController.signal });
      document.addEventListener('input', (event) => {
        if (!this.scope?.contains(event.target)) return;
        if (event.target.name === 'quantity' || event.target.closest('quantity-selector')) {
          this.currentQuantity = Math.max(1, parseInt(event.target.value, 10) || 1);
          this.updateRequirement();
        }
      }, { capture: true, signal: this.eventController.signal });
      document.addEventListener('quantity-selector:update', (event) => {
        if (!this.scope?.contains(event.target)) return;
        this.currentQuantity = Math.max(1, parseInt(event.detail?.quantity, 10) || this.findQuantity());
        this.updateRequirement();
      }, { signal: this.eventController.signal });
      ['variant:change', 'product:variant-change'].forEach((name) => document.addEventListener(name, (event) => {
        if (event.target && !this.scope?.contains(event.target)) return;
        const eventVariantId = event.detail?.variant?.id || event.detail?.variantId;
        if (eventVariantId) this.activeVariantId = String(eventVariantId);
        setTimeout(() => { this.activeVariantId = this.findVariantId() || this.activeVariantId; this.updateRequirement(); }, 0);
      }, { signal: this.eventController.signal }));

      this.form.addEventListener('submit', (event) => {
        this.updateRequirement();
        if (!this.isComplete()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showRequiredError();
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);

      const purchaseArea = this.form.closest('.shopify-section, [data-section-id]') || this.form.parentElement;
      purchaseArea.addEventListener('click', (event) => {
        const button = event.target.closest('button[type="submit"], input[type="submit"], .shopify-payment-button__button, .sticky-add-to-cart__button');
        if (button && !this.isComplete()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showRequiredError();
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }

    selectedVariant() {
      return this.variants[this.activeVariantId] || {};
    }

    quantity() {
      return Math.max(1, this.currentQuantity || this.findQuantity());
    }

    findVariantId() {
      // Horizon updates the visible variant picker before its hidden cart-form
      // variant input. Resolve every selected option as one exact combination;
      // an individual Size option can otherwise point at its representative
      // first variant (for example, 2x2 Square / Pack of 1).
      const picker = this.scope?.querySelector('variant-picker');
      const selectedOptions = picker
        ? Array.from(picker.querySelectorAll('select option:checked, fieldset input:checked'))
        : [];
      const selectedValues = selectedOptions.map((option) => String(option.value || '').trim());
      if (selectedValues.length) {
        const exactMatch = Object.entries(this.variants).find(([, variant]) => {
          if (!Array.isArray(variant.options) || variant.options.length !== selectedValues.length) return false;
          return variant.options.every((value, index) => String(value).trim() === selectedValues[index]);
        });
        if (exactMatch) return exactMatch[0];
      }
      // Single-option products and brief intermediate states can still use the
      // option's variant ID before Horizon finishes updating the full picker.
      const selected = selectedOptions[selectedOptions.length - 1];
      const pickerValue = selected?.dataset?.variantId;
      if (pickerValue && this.variants[String(pickerValue)]) return String(pickerValue);
      const formId = this.form?.id;
      const formControl = formId ? document.querySelector(`[name="id"][form="${CSS.escape(formId)}"]`) : null;
      const formValue = this.form?.querySelector('[name="id"]')?.value || formControl?.value;
      if (formValue && this.variants[String(formValue)]) return String(formValue);
      const urlValue = new URL(window.location.href).searchParams.get('variant');
      if (urlValue && this.variants[String(urlValue)]) return String(urlValue);
      return Object.keys(this.variants)[0] || '';
    }

    findQuantity() {
      const formId = this.form?.id;
      const formControl = formId ? document.querySelector(`[name="quantity"][form="${CSS.escape(formId)}"]`) : null;
      const control = this.scope?.querySelector('[name="quantity"]') || this.form?.querySelector('[name="quantity"]') || formControl;
      return Math.max(1, parseInt(control?.value || '1', 10) || 1);
    }

    packSize() {
      const variant = this.selectedVariant();
      const metafieldValue = parseInt(variant.required, 10);
      if (metafieldValue > 0) return metafieldValue;
      const match = String(variant.title || '').match(/(?:pack(?:\s+of)?\s*)?(\d+)/i);
      return match ? Math.max(1, parseInt(match[1], 10)) : 1;
    }

    updateRequirement() {
      const nextRequired = this.packSize() * this.quantity();
      const requirementChanged = nextRequired !== this.required;
      this.required = nextRequired;
      if (requirementChanged && this.repeatToggle?.checked) this.redistributeCopies();
      this.render();
    }

    addFiles(incoming) {
      this.clearError();
      for (const file of incoming) {
        if (!this.validateFile(file)) continue;
        if (this.files.length >= this.required) {
          this.showError(this.template(this.config.tooManyText || 'You can upload only {required} photos for this selection.', { required: this.required }));
          break;
        }
        this.files.push(file);
        this.initializeMeta(file);
      }
      if (this.repeatToggle.checked) this.redistributeCopies();
      this.render();
    }

    validateFile(file) {
      if (!file || !this.isImageFile(file)) {
        this.showError(this.config.errorType || 'Please select image files only.');
        return false;
      }
      const maxBytes = (parseInt(this.config.maxSize, 10) || 10) * 1024 * 1024;
      if (file.size > maxBytes) {
        this.showError(this.template(this.config.errorSize || 'Each photo must be smaller than {max} MB.', { max: this.config.maxSize || 10 }));
        return false;
      }
      return true;
    }

    initializeMeta(file, previous = {}) {
      this.fileMeta.set(file, {
        copies: Math.max(1, parseInt(previous.copies, 10) || 1),
        note: previous.note || '',
        width: 0,
        height: 0
      });
      this.readDimensions(file);
    }

    readDimensions(file) {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        const meta = this.fileMeta.get(file);
        if (meta) { meta.width = image.naturalWidth; meta.height = image.naturalHeight; }
        URL.revokeObjectURL(url);
        clearTimeout(this.dimensionRenderTimer);
        this.dimensionRenderTimer = setTimeout(() => { if (this.isConnected) this.render(); }, 100);
      };
      image.onerror = () => URL.revokeObjectURL(url);
      image.src = url;
    }

    replaceFile(index, file) {
      this.clearError();
      if (!this.validateFile(file) || !this.files[index]) return;
      const previous = this.fileMeta.get(this.files[index]) || {};
      this.files[index] = file;
      this.initializeMeta(file, previous);
      this.render();
    }

    isImageFile(file) {
      const allowedTypes = new Set([
        'image/avif', 'image/gif', 'image/heic', 'image/heif',
        'image/jpeg', 'image/png', 'image/webp'
      ]);
      if (file.type && allowedTypes.has(file.type.toLowerCase())) return true;
      // Some mobile browsers/camera exports provide an empty MIME type.
      return /\.(avif|gif|heic|heif|jpe?g|png|webp)$/i.test(file.name || '');
    }

    removeFile(index) {
      this.files.splice(index, 1);
      if (this.repeatToggle.checked) this.redistributeCopies();
      this.clearError();
      this.render();
    }

    redistributeCopies() {
      if (!this.repeatToggle.checked || !this.files.length) return;
      const base = Math.floor(this.required / this.files.length);
      let remainder = this.required % this.files.length;
      this.files.forEach((file) => {
        const meta = this.fileMeta.get(file) || {};
        meta.copies = Math.max(1, base + (remainder-- > 0 ? 1 : 0));
        this.fileMeta.set(file, meta);
      });
    }

    moveFile(from, to) {
      if (from === to || from < 0 || to < 0 || from >= this.files.length || to >= this.files.length) return;
      const moved = this.files.splice(from, 1)[0];
      this.files.splice(to, 0, moved);
      this.render();
    }

    syncFileProperties() {
      this.form.querySelectorAll('[data-onhow-property]').forEach((node) => node.remove());
      const label = this.config.label || 'Photo';
      if (this.repeatToggle.checked && this.files.length) {
        const photoWord = this.files.length === 1 ? 'photo' : 'photos';
        this.appendTextProperty('_Production summary', `${this.required} magnets from ${this.files.length} ${photoWord}`);
      }
      this.files.forEach((file, index) => {
        const input = document.createElement('input');
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.type = 'file';
        input.name = `properties[${label} ${index + 1}]`;
        input.files = transfer.files;
        input.hidden = true;
        input.dataset.onhowProperty = '';
        this.form.appendChild(input);
        const meta = this.fileMeta.get(file) || {};
        if (this.repeatToggle.checked) this.appendTextProperty(`_Copies for ${label} ${index + 1}`, meta.copies || 1);
        if (meta.note?.trim()) this.appendTextProperty(`${label} ${index + 1} note`, meta.note.trim());
      });
    }

    appendTextProperty(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = String(value);
      input.dataset.onhowProperty = '';
      this.form.appendChild(input);
    }

    render() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = [];
      this.previews.replaceChildren();
      this.files.forEach((file, index) => {
        const item = document.createElement('div');
        const imageWrap = document.createElement('div');
        const image = document.createElement('img');
        const remove = document.createElement('button');
        const badge = document.createElement('span');
        const controls = document.createElement('div');
        const buttonRow = document.createElement('div');
        const moveBack = document.createElement('button');
        const moveForward = document.createElement('button');
        const replace = document.createElement('button');
        const noteLabel = document.createElement('label');
        const noteInput = document.createElement('input');
        const meta = this.fileMeta.get(file) || { copies: 1, note: '', width: 0, height: 0 };
        const url = URL.createObjectURL(file);
        this.objectUrls.push(url);
        item.className = 'onhow-photo-upload__preview';
        item.draggable = true;
        item.addEventListener('dragstart', () => { this.dragIndex = index; item.classList.add('is-dragging'); });
        item.addEventListener('dragend', () => { this.dragIndex = null; item.classList.remove('is-dragging'); });
        item.addEventListener('dragover', (event) => event.preventDefault());
        item.addEventListener('drop', (event) => { event.preventDefault(); this.moveFile(this.dragIndex, index); });
        imageWrap.className = 'onhow-photo-upload__image-wrap';
        image.src = url; image.alt = `Selected photo ${index + 1}`; image.title = file.name;
        remove.type = 'button'; remove.className = 'onhow-photo-upload__remove'; remove.innerHTML = '&times;';
        remove.setAttribute('aria-label', `Remove photo ${index + 1}`);
        remove.addEventListener('click', () => this.removeFile(index));
        badge.className = 'onhow-photo-upload__index'; badge.textContent = `${index + 1}`;
        imageWrap.append(image, remove, badge);

        controls.className = 'onhow-photo-upload__controls';
        buttonRow.className = 'onhow-photo-upload__control-row';
        [moveBack, moveForward, replace].forEach((button) => { button.type = 'button'; button.className = 'onhow-photo-upload__small-button'; });
        moveBack.textContent = '←'; moveBack.title = 'Move photo left'; moveBack.setAttribute('aria-label', `Move photo ${index + 1} left`);
        moveBack.disabled = index === 0; moveBack.addEventListener('click', () => this.moveFile(index, index - 1));
        moveForward.textContent = '→'; moveForward.title = 'Move photo right'; moveForward.setAttribute('aria-label', `Move photo ${index + 1} right`);
        moveForward.disabled = index === this.files.length - 1; moveForward.addEventListener('click', () => this.moveFile(index, index + 1));
        replace.textContent = 'Replace'; replace.title = `Replace photo ${index + 1}`; replace.setAttribute('aria-label', `Replace photo ${index + 1}`);
        replace.classList.add('onhow-photo-upload__small-button--replace');
        replace.addEventListener('click', () => { this.replaceIndex = index; this.input.click(); });
        buttonRow.append(moveBack, replace, moveForward);
        controls.appendChild(buttonRow);

        if (this.repeatToggle.checked) {
          const copiesLabel = document.createElement('label');
          const copiesInput = document.createElement('input');
          copiesLabel.className = 'onhow-photo-upload__copies'; copiesLabel.append('Magnet copies');
          copiesInput.type = 'number'; copiesInput.min = '1'; copiesInput.max = String(this.required); copiesInput.value = String(meta.copies || 1);
          copiesInput.addEventListener('change', () => {
            meta.copies = Math.min(this.required, Math.max(1, parseInt(copiesInput.value, 10) || 1));
            this.fileMeta.set(file, meta); this.clearError(); this.render();
          });
          copiesLabel.appendChild(copiesInput); controls.appendChild(copiesLabel);
        }

        noteLabel.className = 'onhow-photo-upload__note'; noteLabel.append('Optional note');
        noteInput.type = 'text'; noteInput.maxLength = 150; noteInput.placeholder = 'e.g. Keep both faces visible'; noteInput.value = meta.note || '';
        noteInput.addEventListener('input', () => { meta.note = noteInput.value; this.fileMeta.set(file, meta); });
        noteInput.addEventListener('change', () => this.syncFileProperties());
        noteLabel.appendChild(noteInput); controls.appendChild(noteLabel);

        const minWidth = parseInt(this.config.minWidth, 10) || 1000;
        const minHeight = parseInt(this.config.minHeight, 10) || 1000;
        if (meta.width && meta.height && (meta.width < minWidth || meta.height < minHeight)) {
          const warning = document.createElement('p');
          warning.className = 'onhow-photo-upload__resolution';
          warning.textContent = this.template(
            this.config.resolutionText || 'This photo may look blurry when printed ({width} × {height}px).',
            { width: meta.width, height: meta.height }
          );
          controls.appendChild(warning);
        }

        item.append(imageWrap, controls); this.previews.appendChild(item);
      });
      const assigned = this.assignedCopies();
      this.count.textContent = this.repeatToggle.checked
        ? `${this.files.length} photo${this.files.length === 1 ? '' : 's'} • ${assigned} of ${this.required} magnets assigned`
        : this.template(this.config.countText || '{current} of {required} photos uploaded', { current: this.files.length, required: this.required });
      this.classList.toggle('is-complete', this.isComplete());
      this.complete.hidden = !this.isComplete();
      this.complete.textContent = this.isComplete()
        ? this.template(this.config.completeText || 'All {required} magnets are assigned. You can now add this pack to your cart.', { required: this.required })
        : '';
      this.syncFileProperties();
      this.updateButtons();
      if (this.files.length > this.required) {
        this.showError(this.template(
          this.config.tooManyText || 'You can upload only {required} photos for this selection.',
          { required: this.required }
        ));
        this.error.dataset.onhowOverflow = 'true';
      } else if (this.error.dataset.onhowOverflow === 'true') {
        this.clearError();
      }
    }

    assignedCopies() {
      return this.files.reduce((total, file) => total + (parseInt(this.fileMeta.get(file)?.copies, 10) || 1), 0);
    }

    isComplete() {
      if (this.repeatToggle?.checked) return this.files.length > 0 && this.assignedCopies() === this.required;
      return this.files.length === this.required;
    }

    updateButtons() {
      const blocked = !this.isComplete();
      this.form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
        button.dataset.onhowPurchaseBlocked = String(blocked);
        if (blocked) button.setAttribute('aria-disabled', 'true');
        else if (!button.disabled) button.removeAttribute('aria-disabled');
      });
      const area = this.form.closest('.shopify-section, [data-section-id]') || this.form.parentElement;
      area?.querySelectorAll('.shopify-payment-button__button, .sticky-add-to-cart__button').forEach((button) => {
        button.dataset.onhowPurchaseBlocked = String(blocked);
        if (blocked) button.setAttribute('aria-disabled', 'true');
        else if (!button.disabled) button.removeAttribute('aria-disabled');
      });
    }

    observePaymentButton() {
      const area = this.form.closest('.shopify-section, [data-section-id]') || this.form;
      this.observer = new MutationObserver(() => this.updateButtons());
      this.observer.observe(area, { childList: true, subtree: true });
    }

    showRequiredError() {
      const message = this.repeatToggle.checked
        ? 'Please assign exactly {required} magnet copies before continuing.'
        : (this.config.requiredText || 'Please upload exactly {required} photos before continuing.');
      this.showError(this.template(message, { required: this.required }));
    }
    showError(message) { this.error.textContent = message; this.error.hidden = false; }
    clearError() { this.error.textContent = ''; this.error.hidden = true; delete this.error.dataset.onhowOverflow; }
    template(text, values) {
      return Object.keys(values).reduce((result, key) => result.replaceAll(`{${key}}`, values[key]), text);
    }
  }

  customElements.define('onhow-photo-upload', OnhowPhotoUpload);
})();
